import * as React from "react"
import { CheckCircle2, XCircle, PlusCircle, Loader2, Search, Eye } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { AdminCard } from "./AdminCard"
import { useAuth, type AdminPermissions, type UserRole } from "@/contexts/AuthContext"

interface AdminSettingsProps {
  email: string
  role: UserRole | null
  permissions: AdminPermissions | null
}

const permissionLabels: Array<{ key: keyof AdminPermissions; label: string }> = [
  { key: "canViewAdminDashboard", label: "View Admin Dashboard" },
  { key: "canManageUsers", label: "Manage Users" },
  { key: "canModerateContent", label: "Moderate Content" },
  { key: "canManageSystemSettings", label: "Manage System Settings" },
]

const userTypeLabel = (userType: "agent" | "solo_agent" | "agency" | null) => {
  if (userType === "solo_agent") return "Solo Agent"
  if (userType === "agent") return "Agent"
  if (userType === "agency") return "Agency"
  return "Unstarted"
}

export function AdminSettings({ email, role, permissions }: AdminSettingsProps) {
  const navigate = useNavigate()
  const { adjustAdminUserEconomics, searchAdminUsers, startInvestigateMode } = useAuth()
  const [targetEmail, setTargetEmail] = React.useState("")
  const [pointsDelta, setPointsDelta] = React.useState("0")
  const [bonusPendingDelta, setBonusPendingDelta] = React.useState("0")
  const [bonusAvailableDelta, setBonusAvailableDelta] = React.useState("0")
  const [note, setNote] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [result, setResult] = React.useState<{
    type: "success" | "error"
    text: string
    user?: {
      email: string
      pointsCurrent: number
      pointsLifetime: number
      bonusPending: number
      bonusAvailable: number
    }
  } | null>(null)
  const [customerQuery, setCustomerQuery] = React.useState("")
  const [customerSearchResults, setCustomerSearchResults] = React.useState<Array<{
    _id: string
    email: string
    displayName: string
    agencyName: string | null
    userType: "agent" | "solo_agent" | "agency" | null
    hasCompletedOnboarding: boolean
    emailVerified: boolean
    createdAt: string
  }>>([])
  const [customerSearchFeedback, setCustomerSearchFeedback] = React.useState<{
    type: "error" | "info"
    text: string
  } | null>(null)
  const [isSearchingCustomers, setIsSearchingCustomers] = React.useState(false)
  const [investigatingUserId, setInvestigatingUserId] = React.useState<string | null>(null)

  const isSuperAdmin = role === "super_admin"

  const quickPointButtons = [50, 100, 250, 500]

  const handleCustomerSearch = async () => {
    const query = customerQuery.trim()
    if (query.length < 2) {
      setCustomerSearchResults([])
      setCustomerSearchFeedback({ type: "error", text: "Enter at least 2 characters (name or email)." })
      return
    }

    setIsSearchingCustomers(true)
    setCustomerSearchFeedback(null)
    const response = await searchAdminUsers(query)
    if (response.success) {
      const users = response.users || []
      setCustomerSearchResults(users)
      setCustomerSearchFeedback(
        users.length === 0
          ? { type: "info", text: "No matching customers found." }
          : { type: "info", text: `Found ${users.length} customer${users.length === 1 ? "" : "s"}.` }
      )
    } else {
      setCustomerSearchResults([])
      setCustomerSearchFeedback({ type: "error", text: response.message || "Failed to search customers." })
    }
    setIsSearchingCustomers(false)
  }

  const handleStartInvestigate = async (targetUserId: string) => {
    setInvestigatingUserId(targetUserId)
    setCustomerSearchFeedback(null)
    const response = await startInvestigateMode(targetUserId)
    if (response.success) {
      navigate("/dashboard", { replace: true })
      return
    }

    setCustomerSearchFeedback({ type: "error", text: response.message || "Failed to start investigate mode." })
    setInvestigatingUserId(null)
  }

  const handleApplyAdjustment = async () => {
    if (!isSuperAdmin) {
      setResult({ type: "error", text: "Only super admins can apply manual adjustments." })
      return
    }

    const parsedPointsDelta = Number(pointsDelta || "0")
    const parsedBonusPendingDelta = Number(bonusPendingDelta || "0")
    const parsedBonusAvailableDelta = Number(bonusAvailableDelta || "0")

    if (!targetEmail.trim()) {
      setResult({ type: "error", text: "Target account email is required." })
      return
    }

    if (
      !Number.isFinite(parsedPointsDelta) ||
      !Number.isFinite(parsedBonusPendingDelta) ||
      !Number.isFinite(parsedBonusAvailableDelta)
    ) {
      setResult({ type: "error", text: "All delta values must be valid numbers." })
      return
    }

    if (parsedPointsDelta === 0 && parsedBonusPendingDelta === 0 && parsedBonusAvailableDelta === 0) {
      setResult({ type: "error", text: "Enter at least one non-zero adjustment." })
      return
    }

    setIsSubmitting(true)
    setResult(null)
    const response = await adjustAdminUserEconomics({
      email: targetEmail.trim(),
      pointsDelta: parsedPointsDelta,
      bonusPendingDelta: parsedBonusPendingDelta,
      bonusAvailableDelta: parsedBonusAvailableDelta,
      note: note.trim() || undefined,
    })

    if (response.success && response.user) {
      setResult({
        type: "success",
        text: response.message || "Manual adjustment applied.",
        user: response.user,
      })
      setPointsDelta("0")
      setBonusPendingDelta("0")
      setBonusAvailableDelta("0")
      setNote("")
    } else {
      setResult({ type: "error", text: response.message || "Failed to apply manual adjustment." })
    }
    setIsSubmitting(false)
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Session Info */}
      <AdminCard delay={0}>
        <h3 className="text-sm font-semibold text-white/70 font-admin mb-4">Session Info</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between py-2 border-b border-[#2A2A45]/50">
            <span className="text-xs uppercase tracking-wider text-white/30 font-admin">Email</span>
            <span className="text-sm text-white/70 font-mono-admin">{email}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-xs uppercase tracking-wider text-white/30 font-admin">Role</span>
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#F37020]/10 text-[#F37020] font-admin">
              {role === "super_admin" ? "Super Admin" : "Support"}
            </span>
          </div>
        </div>
      </AdminCard>

      {/* Permissions */}
      {permissions && (
        <AdminCard delay={0.1}>
          <h3 className="text-sm font-semibold text-white/70 font-admin mb-4">Permissions</h3>
          <div className="space-y-2">
            {permissionLabels.map(({ key, label }) => {
              const allowed = permissions[key]
              return (
                <div
                  key={key}
                  className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-[#0C0C12] border border-[#2A2A45]/50"
                >
                  <span className="text-sm text-white/60 font-admin">{label}</span>
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-medium ${
                      allowed ? "text-emerald-400" : "text-white/20"
                    }`}
                  >
                    {allowed ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <XCircle className="w-3.5 h-3.5" />
                    )}
                    {allowed ? "Allowed" : "Restricted"}
                  </span>
                </div>
              )
            })}
          </div>
        </AdminCard>
      )}

      <AdminCard delay={0.15}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-white/80 font-admin">Customer Investigate Mode</h3>
            <p className="text-xs text-white/35 mt-1 font-admin">
              Search customers by name or email, then open an investigate session to see what they see.
            </p>
          </div>
          <span className="text-[11px] uppercase tracking-wider text-[#F37020]/90 border border-[#F37020]/20 bg-[#F37020]/10 rounded-full px-2 py-1 font-admin">
            Support + Super Admin
          </span>
        </div>

        <div className="mt-4 flex flex-col md:flex-row gap-3">
          <label className="flex-1 text-xs text-white/40 font-admin space-y-1">
            Customer Name or Email
            <div className="flex items-center gap-2">
              <input
                value={customerQuery}
                onChange={(e) => setCustomerQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    if (!isSearchingCustomers) handleCustomerSearch()
                  }
                }}
                placeholder="Jane Doe or jane@agency.com"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
                disabled={isSearchingCustomers || !!investigatingUserId}
              />
              <button
                type="button"
                onClick={handleCustomerSearch}
                disabled={isSearchingCustomers || !!investigatingUserId}
                className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/[0.04] border border-[#2A2A45] text-sm text-white/70 hover:text-white hover:border-[#F37020]/35 transition-colors disabled:opacity-60 font-admin"
              >
                {isSearchingCustomers ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                Search
              </button>
            </div>
          </label>
        </div>

        {customerSearchFeedback && (
          <div
            className={`mt-3 rounded-lg border px-3 py-2 text-xs font-admin ${
              customerSearchFeedback.type === "error"
                ? "border-red-500/20 bg-red-500/10 text-red-300"
                : "border-white/10 bg-white/[0.03] text-white/55"
            }`}
          >
            {customerSearchFeedback.text}
          </div>
        )}

        {customerSearchResults.length > 0 && (
          <div className="mt-4 space-y-2">
            {customerSearchResults.map((customer) => (
              <div
                key={customer._id}
                className="rounded-xl border border-[#2A2A45]/70 bg-[#0C0C12] px-3 py-3 flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm text-white/85 font-admin">{customer.displayName}</span>
                    <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border border-[#F37020]/25 bg-[#F37020]/10 text-[#F59A58] font-admin">
                      {userTypeLabel(customer.userType)}
                    </span>
                    {!customer.emailVerified && (
                      <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border border-amber-400/20 bg-amber-400/10 text-amber-300 font-admin">
                        Unverified
                      </span>
                    )}
                    {!customer.hasCompletedOnboarding && (
                      <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border border-white/10 bg-white/[0.05] text-white/50 font-admin">
                        Onboarding
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-white/45 mt-1 font-admin break-all">{customer.email}</p>
                  {customer.agencyName && (
                    <p className="text-xs text-white/30 mt-1 font-admin">Agency: {customer.agencyName}</p>
                  )}
                </div>

                <button
                  type="button"
                  onClick={() => handleStartInvestigate(customer._id)}
                  disabled={!!investigatingUserId}
                  className="shrink-0 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-[#F37020]/15 border border-[#F37020]/25 text-sm text-[#F59A58] hover:bg-[#F37020]/25 transition-colors disabled:opacity-60 font-admin"
                >
                  {investigatingUserId === customer._id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Eye className="w-4 h-4" />
                  )}
                  {investigatingUserId === customer._id ? "Starting..." : "Investigate"}
                </button>
              </div>
            ))}
          </div>
        )}
      </AdminCard>

      <AdminCard delay={0.2}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="text-sm font-semibold text-white/80 font-admin">Manual Points & Bonus Adjustments</h3>
            <p className="text-xs text-white/35 mt-1 font-admin">
              Apply one-time manual credits or corrections to a specific account.
            </p>
            {!isSuperAdmin && (
              <p className="text-xs text-amber-300/90 mt-1 font-admin">
                View-only for support admins. Ask a super admin to apply changes.
              </p>
            )}
          </div>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block text-xs text-white/40 font-admin space-y-1">
            Account Email
            <input
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value)}
              placeholder="agent@agency.com"
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              disabled={isSubmitting || !isSuperAdmin}
            />
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <label className="block text-xs text-white/40 font-admin space-y-1">
              Points Delta
              <input
                value={pointsDelta}
                onChange={(e) => setPointsDelta(e.target.value)}
                placeholder="100"
                inputMode="numeric"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
                disabled={isSubmitting || !isSuperAdmin}
              />
            </label>

            <label className="block text-xs text-white/40 font-admin space-y-1">
              Bonus Pending Delta ($)
              <input
                value={bonusPendingDelta}
                onChange={(e) => setBonusPendingDelta(e.target.value)}
                placeholder="25.00"
                inputMode="decimal"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
                disabled={isSubmitting || !isSuperAdmin}
              />
            </label>

            <label className="block text-xs text-white/40 font-admin space-y-1">
              Bonus Available Delta ($)
              <input
                value={bonusAvailableDelta}
                onChange={(e) => setBonusAvailableDelta(e.target.value)}
                placeholder="25.00"
                inputMode="decimal"
                className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
                disabled={isSubmitting || !isSuperAdmin}
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-2">
            {quickPointButtons.map((points) => (
              <button
                key={`quick-points-${points}`}
                type="button"
                onClick={() => setPointsDelta((prev) => String((Number(prev || "0") || 0) + points))}
                className="px-2.5 py-1 rounded-md border border-[#2A2A45] bg-[#0C0C12] text-[11px] text-white/60 hover:text-white/80 hover:border-[#F37020]/40 transition-colors font-admin disabled:opacity-50"
                disabled={isSubmitting || !isSuperAdmin}
              >
                +{points} pts
              </button>
            ))}
          </div>

          <label className="block text-xs text-white/40 font-admin space-y-1">
            Note (optional)
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
              placeholder="Reason for manual adjustment"
              className="w-full px-3 py-2 rounded-lg bg-[#0C0C12] border border-[#2A2A45] text-sm text-white/70 font-admin placeholder:text-white/20 outline-none focus:border-[#F37020]/40 transition-colors"
              disabled={isSubmitting || !isSuperAdmin}
            />
          </label>
        </div>

        {result && (
          <div
            className={`mt-4 px-3 py-2 rounded-lg text-xs font-medium ${
              result.type === "success"
                ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20"
                : "bg-red-500/10 text-red-400 border border-red-500/20"
            }`}
          >
            <div>{result.text}</div>
            {result.user && (
              <div className="mt-2 text-[11px] text-white/65 font-admin space-y-1">
                <div>{result.user.email}</div>
                <div>
                  Points: {result.user.pointsCurrent.toLocaleString()} current / {result.user.pointsLifetime.toLocaleString()} lifetime
                </div>
                <div>
                  Bonus: ${result.user.bonusPending.toFixed(2)} pending / ${result.user.bonusAvailable.toFixed(2)} available
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between">
          <span className="text-[11px] text-white/20 font-admin">Use negative values for corrections.</span>
          <button
            type="button"
            onClick={handleApplyAdjustment}
            disabled={isSubmitting || !isSuperAdmin}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#F37020]/20 border border-[#F37020]/30 text-sm text-[#F37020] hover:bg-[#F37020]/30 transition-colors disabled:opacity-60 font-admin"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
            Apply Adjustment
          </button>
        </div>
      </AdminCard>
    </div>
  )
}
